import styled from "@emotion/styled";
import { Close } from "@mui/icons-material";
import { Button, Drawer, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Coins } from "@phosphor-icons/react";
import { useChurchStore } from "features/churches";
import { useRequisitions } from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import moment from "moment";
import { useMemo } from "react";

const Banner = styled.div`
  border-radius: 8px;
  background: #7fa457;
  color: #fff;
  padding: 20px;
  display: flex;
  align-items: center;
`;

const BannerTitle = styled.span`
  font-size: 12px;
  font-weight: 500;
`;

const AmountFigure = styled.span`
  font-size: 12px;
`;

const Figure = styled.span`
  font-size: 32px;
  font-weight: 700;
`;

export const PayRequests = ({ currentRequisitionId, open, onClose }) => {
  const selectedChurch = useChurchStore(state => state.selectedChurch);

  const { data: requisitions } = useRequisitions({
    churchId: selectedChurch?.id,
    queries: {
      filter: [
        {
          column: "requisition_status",
          operator: FILTER_OPERATIONS.IN,
          value: ["ACCOUNTANT", "SCHEDULED"]
        }
      ]
    }
  });

  const scheduledPayments = useMemo(() => {
    return (
      requisitions?.result?.filter(
        requisition => String(requisition.id) !== String(currentRequisitionId)
      ) || []
    );
  }, [requisitions]);

  const totalPayableAmount = useMemo(() => {
    return scheduledPayments.reduce((acc, curr) => acc + curr.amount, 0);
  }, [scheduledPayments]);

  const columns = [
    {
      field: "requestedBy",
      headerName: "Requested by",
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <>
            {row.user?.first_name} {row.user?.last_name}
          </>
        );
      }
    },
    { field: "amount", headerName: "Total amount", flex: 1 },
    {
      field: "department",
      headerName: "Department",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{row.team?.name}</>;
      }
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{moment(row.due_date).format("YYYY-MM-DD")}</>;
      }
    }
  ];

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Stack sx={{ width: 900, p: 3 }} spacing={2}>
        <Stack direction="row" justifyContent="flex-start">
          <Button
            startIcon={<Close />}
            variant="text"
            color="error"
            onClick={onClose}
          >
            Close
          </Button>
        </Stack>
        <Banner>
          <Stack flex={1}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <BannerTitle>Total Balance</BannerTitle>
              <Coins
                size={32}
                style={{
                  mixBlendMode: "soft-light"
                }}
              />
            </Stack>
            <AmountFigure>
              <Figure>0</Figure> ETB
            </AmountFigure>
          </Stack>
        </Banner>
        <Typography variant="body2">Payable Requests</Typography>
        <Stack>
          <DataGrid
            autoHeight
            rows={scheduledPayments}
            columns={columns}
            density="comfortable"
            disableColumnMenu
            disableSelectionOnClick
            hideFooter
            sx={{
              margin: 0
            }}
          />
          <Stack
            sx={{
              backgroundColor: "#f9f9f9",
              p: 2
            }}
            spacing={1}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <BannerTitle>Total payable amount</BannerTitle>
              <AmountFigure>
                <Figure
                  style={{
                    fontSize: "16px",
                    color: "#00B0B9"
                  }}
                >
                  {totalPayableAmount.toLocaleString()}
                </Figure>{" "}
                ETB
              </AmountFigure>
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <BannerTitle>Amount left after payed</BannerTitle>
              <AmountFigure>
                <Figure
                  style={{
                    fontSize: "16px",
                    color: "#00B0B9"
                  }}
                >
                  0
                </Figure>{" "}
                ETB
              </AmountFigure>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Drawer>
  );
};
